/**
 * The launchpad's Home: one card per top-level folder (screens that sit at the
 * top level share a "Screens" card), each listing its folder's entries. A
 * folder DRILLS DOWN IN PLACE inside its card — to any depth, with a back
 * button and a clickable trail — and a screen opens as a tab. Beside the
 * cards: the open tabs ("Continue where you left off"), Open editors, Pinned
 * and Recently opened.
 *
 * Which folder each card shows is the only state kept here, and it is view
 * state: it is owned by the layout (so the screen title's breadcrumb can
 * point a card at a folder) and passed in.
 */
import { ChevronLeft, ChevronRight, Clock, Layers, Pencil, Star } from 'lucide-react'
import { useEffect, useId, useRef, type CSSProperties, type ReactNode } from 'react'
import { tw } from '../../../testHooks'
import { EditorsList } from '../../nav/parts/EditorsList'
import { PinnedList, RecentList, useEntryMenu } from '../../nav/parts/ShellLists'
import { NavIcon } from '../../nav/navIcons'
import { S } from '../../nav/tokens'
import type { NavTreeNode } from '../../nav/tree'
import type { RunNavItem } from '../../nav/types'
import type { ShellNavModel } from '../../nav/useShellNav'

/** The card that collects the top-level entries that are not folders. */
export const LOOSE_CARD = '__screens'

/** Card tones — theme variables only, so light and dark both come right.
 *  Tints sit behind ICONS only (non-text, 3:1 is the bar); text stays S.text. */
const TONES: { ink: string; tint: string }[] = [
  { ink: S.accent, tint: S.accentWeak },
  { ink: 'var(--color-success, #1a7f37)', tint: 'var(--color-success-weak, rgba(26, 127, 55, 0.10))' },
  { ink: S.warning, tint: S.warningWeak },
  { ink: S.text2, tint: S.surface2 },
]

export interface CardDef {
  id: string
  title: string
  /** The folder the card stands for (null for the loose "Screens" card). */
  node: NavTreeNode | null
  /** What the card lists before any drill-down. */
  base: NavTreeNode[]
}

/** Folders first-class as cards; top-level screens and websites gathered into one card. */
export function buildCards(roots: NavTreeNode[]): CardDef[] {
  const loose = roots.filter((r) => !r.isFolder)
  const cards: CardDef[] = roots.filter((r) => r.isFolder)
    .map((r) => ({ id: r.item.id, title: r.item.title, node: r, base: r.children }))
  if (loose.length) cards.unshift({ id: LOOSE_CARD, title: 'Screens', node: null, base: loose })
  return cards
}

/** The drill-down trail a card's stored ids still resolve to (a folder that
 *  left the menu — rights, an edit — simply ends the trail there). */
export function resolveTrail(base: NavTreeNode[], ids: string[] | undefined): NavTreeNode[] {
  const trail: NavTreeNode[] = []
  let level = base
  for (const id of ids || []) {
    const n = level.find((x) => x.item.id === id && x.isContainer)
    if (!n) break
    trail.push(n)
    level = n.children
  }
  return trail
}

/** Screens under a node, at any depth. */
function countScreens(node: NavTreeNode): number {
  let c = 0
  const walk = (ns: NavTreeNode[]) => { for (const n of ns) { if (!n.isFolder) c += 1; walk(n.children) } }
  walk(node.children)
  return c
}
const plural = (n: number) => `${n} ${n === 1 ? 'screen' : 'screens'}`

export interface HomeProps {
  model: ShellNavModel
  appTitle: string
  emptyNavMessage: string
  compact: boolean
  panelId: string
  labelledBy: string
  drill: Record<string, string[]>
  setDrill: (cardId: string, ids: string[]) => void
  /** Bumped to bring one card into view (the screen title's breadcrumb). */
  focusCard: { id: string; n: number } | null
  openItem: (item: RunNavItem) => void
  /** After a shared list (Pinned, Recent, Open editors) opened something. */
  onPicked: () => void
  editorsRef: React.RefObject<HTMLElement | null>
}

export function LaunchpadHome({
  model, appTitle, emptyNavMessage, compact, panelId, labelledBy, drill, setDrill, focusCard,
  openItem, onPicked, editorsRef,
}: HomeProps) {
  const entryMenu = useEntryMenu(model)
  const cards = buildCards(model.roots)
  return (
    <div id={panelId} role="tabpanel" aria-labelledby={labelledBy} tabIndex={-1}
      {...tw('screen', 'shell-launchpad-home')}
      style={{ flex: 1, minHeight: 0, overflow: 'auto', background: S.bg, outline: 'none' }}>
      <div style={{ maxWidth: 1360, margin: '0 auto', padding: compact ? '20px 16px 32px' : '28px 32px 40px' }}>
        <div style={{ marginBottom: 22 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: S.text2 }}>Home</div>
          <h1 style={{ margin: '4px 0 0', fontSize: compact ? 24 : 30, fontWeight: 700, letterSpacing: '-0.01em', color: S.text }}>
            {appTitle}
          </h1>
        </div>

        {model.missingObjName && (
          <div role="status" style={{
            marginBottom: 18, padding: '12px 14px', borderRadius: 12, fontSize: 14, lineHeight: 1.5,
            background: S.warningWeak, color: S.text, border: `1px solid ${S.border}`,
          }}>
            "{model.missingObjName}" isn't in this application's navigation, or it isn't available to your roles.
          </div>
        )}

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
          <div style={{ flex: '999 1 600px', minWidth: 0 }}>
            {cards.length === 0 ? (
              <p style={{
                margin: 0, padding: 20, borderRadius: 16, background: S.panel, border: `1px solid ${S.border}`,
                fontSize: 14, color: S.text2, lineHeight: 1.5,
              }}>{emptyNavMessage}</p>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 18 }}>
                {cards.map((c, i) => (
                  <Card key={c.id} card={c} tone={TONES[i % TONES.length]} model={model}
                    trailIds={drill[c.id]} setTrail={(ids) => setDrill(c.id, ids)}
                    focusN={focusCard?.id === c.id ? focusCard.n : 0}
                    openItem={openItem} entryMenu={entryMenu} />
                ))}
              </div>
            )}
          </div>

          <aside aria-label="Your work" style={{ flex: '1 1 300px', maxWidth: 400, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 18 }}>
            <AsideSection title="Continue where you left off" icon={<Layers size={16} aria-hidden />}
              count={model.openScreens.length} style={{ background: S.accentWeak }}>
              {model.openScreens.length === 0 ? (
                <p style={emptyText}>Screens you open stay here as tabs.</p>
              ) : (
                <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {model.openScreens.map((item) => (
                    <div role="listitem" key={item.id}>
                      <button type="button" className="tw-sh-item" onClick={() => openItem(item)}
                        onContextMenu={entryMenu.onContextMenu(item)} onKeyDown={entryMenu.onKeyDown(item)}
                        {...tw('nav', item.obj_name, { type: item.obj_type, folder: false, tab: true })}
                        style={{ ...rowButton, minHeight: 46, background: S.panel }}>
                        <IconBox node={item} tone={TONES[0]} />
                        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0, fontSize: 14.5, fontWeight: 600 }}>{item.title}</span>
                        <span className="tw-sh-clip" style={{ maxWidth: '45%', fontSize: 13, color: S.text2 }}>{model.pathText(item)}</span>
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </AsideSection>

            {model.editorsEnabled && (
              <section ref={editorsRef as React.RefObject<HTMLElement>} aria-label="Open editors" tabIndex={-1}
                {...tw('action', 'shell-launchpad-editors')}
                style={{ ...sectionBox, border: `1.5px dashed ${S.borderStrong}`, outline: 'none' }}>
                <SectionHeading title="Open editors" icon={<Pencil size={16} aria-hidden />} count={model.editors.length} />
                <EditorsList model={model} onPicked={onPicked}
                  emptyText="Records you add or edit and minimise wait here." />
              </section>
            )}

            <AsideSection title="Pinned"
              icon={<Star size={16} aria-hidden fill={S.star} color={S.starStroke} />}>
              <PinnedList model={model} onPicked={onPicked} />
            </AsideSection>

            <AsideSection title="Recently opened" icon={<Clock size={16} aria-hidden />}>
              <RecentList model={model} onPicked={onPicked} />
            </AsideSection>
          </aside>
        </div>
      </div>
      {entryMenu.ui}
    </div>
  )
}

function Card({ card, tone, model, trailIds, setTrail, focusN, openItem, entryMenu }: {
  card: CardDef
  tone: { ink: string; tint: string }
  model: ShellNavModel
  trailIds: string[] | undefined
  setTrail: (ids: string[]) => void
  focusN: number
  openItem: (item: RunNavItem) => void
  entryMenu: ReturnType<typeof useEntryMenu>
}) {
  const headingId = useId()
  const ref = useRef<HTMLElement>(null)
  const trail = resolveTrail(card.base, trailIds)
  const level = trail.length ? trail[trail.length - 1].children : card.base
  const count = card.node ? countScreens(card.node) : card.base.reduce((n, x) => n + (x.isFolder ? 0 : 1) + countScreens(x), 0)

  // Drilling replaces the very button that was pressed — put focus somewhere
  // sensible instead of letting it fall to <body>.
  const pendingFocus = useRef<'back' | 'first' | null>(null)
  useEffect(() => {
    const want = pendingFocus.current
    pendingFocus.current = null
    if (!want || !ref.current) return
    const sel = want === 'back' && trail.length ? '[data-lp-back]' : '[data-lp-row]'
    ref.current.querySelector<HTMLElement>(sel)?.focus()
  }, [trail.length, trail[trail.length - 1]?.item.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // The breadcrumb asked for this card: bring it into view and focus it.
  useEffect(() => {
    if (!focusN || !ref.current) return
    ref.current.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
    ref.current.querySelector<HTMLElement>('[data-lp-back]')?.focus()
  }, [focusN])

  const drillTo = (ids: string[], focus: 'back' | 'first') => { pendingFocus.current = focus; setTrail(ids) }
  const ids = trail.map((t) => t.item.id)
  const parentTitle = trail.length > 1 ? trail[trail.length - 2].item.title : card.title

  return (
    <section ref={ref} aria-labelledby={headingId} {...tw('group', `shell-launchpad-card:${card.id}`)} style={sectionBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '0 4px 12px' }}>
        <span aria-hidden style={{
          width: 42, height: 42, flexShrink: 0, borderRadius: 12, background: tone.tint, color: tone.ink,
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>
          {card.node ? <NavIcon node={card.node.item} size={21} /> : <Layers size={21} />}
        </span>
        <div style={{ minWidth: 0 }}>
          <h2 id={headingId} className="tw-sh-clip" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: S.text }}>{card.title}</h2>
          <div style={{ fontSize: 13, fontWeight: 500, color: S.text2 }}>{plural(count)}</div>
        </div>
      </div>

      {trail.length > 0 && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, margin: '0 0 6px', padding: 6, borderRadius: 12,
          background: tone.tint,
        }}>
          <button type="button" className="tw-sh-btn" data-lp-back=""
            onClick={() => drillTo(ids.slice(0, -1), trail.length > 1 ? 'back' : 'first')}
            aria-label={`Back to ${parentTitle}`} title={`Back to ${parentTitle}`}
            {...tw('action', 'shell-launchpad-back', { card: card.id })}
            style={{
              width: 34, height: 34, flexShrink: 0, border: 'none', borderRadius: 9, background: S.panel,
              color: S.text, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            }}>
            <ChevronLeft size={17} aria-hidden />
          </button>
          <nav aria-label={`${card.title} folder path`} style={{ flex: 1, minWidth: 0 }}>
            <ol style={{ display: 'flex', alignItems: 'center', gap: 2, margin: 0, padding: 0, listStyle: 'none', minWidth: 0, overflow: 'hidden' }}>
              <li style={{ display: 'inline-flex', minWidth: 0, flexShrink: 1 }}>
                <button type="button" className="tw-sh-btn" onClick={() => drillTo([], 'first')}
                  {...tw('action', 'shell-launchpad-trail', { card: card.id, depth: 0 })} style={trailButton}>
                  <span className="tw-sh-clip">{card.title}</span>
                </button>
              </li>
              {trail.map((t, i) => {
                const last = i === trail.length - 1
                return (
                  <li key={t.item.id} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, minWidth: 0, flexShrink: last ? 0 : 1 }}>
                    <span aria-hidden style={{ color: S.text2 }}>›</span>
                    {last ? (
                      <span aria-current="location" className="tw-sh-clip"
                        style={{ ...trailButton, cursor: 'default', fontWeight: 700, color: S.text }}>{t.item.title}</span>
                    ) : (
                      <button type="button" className="tw-sh-btn" onClick={() => drillTo(ids.slice(0, i + 1), 'back')}
                        {...tw('action', 'shell-launchpad-trail', { card: card.id, depth: i + 1, id: t.item.id })}
                        style={trailButton}>
                        <span className="tw-sh-clip">{t.item.title}</span>
                      </button>
                    )}
                  </li>
                )
              })}
            </ol>
          </nav>
        </div>
      )}

      <div role="list" aria-label={trail.length ? trail[trail.length - 1].item.title : card.title}
        style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {level.map((n) => (
          <div role="listitem" key={n.item.id}>
            <Row node={n} tone={tone} model={model} openItem={openItem} entryMenu={entryMenu}
              onDrill={() => drillTo([...ids, n.item.id], 'back')} />
          </div>
        ))}
        {level.length === 0 && (
          <p style={{ ...emptyText, margin: '4px 8px 6px' }}>No screens in this folder yet.</p>
        )}
      </div>
    </section>
  )
}

function Row({ node, tone, model, openItem, entryMenu, onDrill }: {
  node: NavTreeNode
  tone: { ink: string; tint: string }
  model: ShellNavModel
  openItem: (item: RunNavItem) => void
  entryMenu: ReturnType<typeof useEntryMenu>
  onDrill: () => void
}) {
  const { item, isFolder, isContainer, children } = node
  if (isFolder) {
    const n = countScreens(node)
    return (
      <button type="button" className="tw-sh-item" data-lp-row="" onClick={onDrill}
        aria-label={`${item.title}, folder, ${plural(n)}`}
        {...tw('nav', item.obj_name || item.title, { type: item.obj_type, folder: true })}
        style={rowButton}>
        <IconBox node={item} tone={tone} />
        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
        <span style={{ fontSize: 13, fontWeight: 500, color: S.text2, flexShrink: 0 }}>{plural(n)}</span>
        <ChevronRight size={16} aria-hidden color={S.text2} style={{ flexShrink: 0 }} />
      </button>
    )
  }
  const pinned = model.isPinned(item)
  // A website (X) with pages opens on its row; the chevron beside it drills into the pages.
  const drillable = isContainer && children.length > 0
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
      <button type="button" className="tw-sh-item" data-lp-row="" onClick={() => openItem(item)}
        onContextMenu={entryMenu.onContextMenu(item)} onKeyDown={entryMenu.onKeyDown(item)}
        aria-current={model.current?.id === item.id ? 'true' : undefined}
        {...tw('nav', item.obj_name, { type: item.obj_type, folder: false })}
        style={{ ...rowButton, flex: 1 }}>
        <IconBox node={item} tone={tone} />
        <span className="tw-sh-clip" style={{ flex: 1, minWidth: 0 }}>{item.title}</span>
        {pinned && <Star size={14} role="img" aria-label="Pinned" fill={S.star} color={S.starStroke} style={{ flexShrink: 0 }} />}
        {!drillable && <ChevronRight size={16} aria-hidden color={S.text2} style={{ flexShrink: 0 }} />}
      </button>
      {drillable && (
        <button type="button" className="tw-sh-btn" onClick={onDrill}
          aria-label={`Show pages of ${item.title}`} title={`Show pages of ${item.title}`}
          {...tw('action', 'shell-launchpad-drill', { id: item.id })}
          style={{
            width: 44, height: 44, flexShrink: 0, border: 'none', borderRadius: 10, background: 'transparent',
            color: S.text2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
          }}>
          <ChevronRight size={17} aria-hidden />
        </button>
      )}
    </div>
  )
}

function IconBox({ node, tone }: { node: RunNavItem; tone: { ink: string; tint: string } }) {
  return (
    <span aria-hidden style={{
      width: 30, height: 30, flexShrink: 0, borderRadius: 9, background: tone.tint, color: tone.ink,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <NavIcon node={node} size={16} />
    </span>
  )
}

function SectionHeading({ title, icon, count }: { title: string; icon: ReactNode; count?: number }) {
  return (
    <h2 style={{
      display: 'flex', alignItems: 'center', gap: 8, margin: '0 4px 8px', fontSize: 16, fontWeight: 700, color: S.text,
    }}>
      <span aria-hidden style={{ display: 'inline-flex', color: S.text2 }}>{icon}</span>
      {title}
      {typeof count === 'number' && count > 0 && (
        <span style={{
          minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11, background: S.surface2, color: S.text,
          fontSize: 13, fontWeight: 700, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        }}>{count}</span>
      )}
    </h2>
  )
}

function AsideSection({ title, icon, count, style, children }: {
  title: string; icon: ReactNode; count?: number; style?: CSSProperties; children: ReactNode
}) {
  return (
    <section aria-label={title} style={{ ...sectionBox, ...style }}>
      <SectionHeading title={title} icon={icon} count={count} />
      {children}
    </section>
  )
}

const sectionBox: CSSProperties = {
  background: S.panel, border: `1px solid ${S.border}`, borderRadius: 16, padding: '16px 14px 12px',
  minWidth: 0,
}

const rowButton: CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 44, padding: '6px 8px',
  border: 'none', borderRadius: 10, background: 'transparent', color: S.text, fontSize: 14.5, fontWeight: 500,
  textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
}

const trailButton: CSSProperties = {
  display: 'inline-flex', alignItems: 'center', minWidth: 0, maxWidth: '100%', minHeight: 30, padding: '0 6px',
  border: 'none', borderRadius: 7, background: 'transparent', color: S.text, fontSize: 13.5, fontWeight: 600,
  fontFamily: 'inherit', cursor: 'pointer',
}

const emptyText: CSSProperties = { margin: 4, fontSize: 14, color: S.text2, lineHeight: 1.5 }
